<script>
  import { fade, fly } from "svelte/transition";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { TrackingEventStore } from "@shared/svelte/Stores/TrackingEventStore.ts";
  import { uniq } from "lodash";

  let eventData = [];
  let filteredEventData = [];
  let filterEntity = "";
  let filterEntityTypes = [];
  let filterEvent = "";
  let filterEventTypes = [];

  $: eventData = $TrackingEventStore;

  let show = true;
  const slideOver = () => {
    show = !show;
  };

  let collapsed = true;
  const toggleCollapse = () => {
    collapsed = !collapsed;
  };

  const clearEvents = () => {
    eventData = [];
    TrackingEventStore.update(() => []);
  };

  let sessionId = "";
  let eventCount = 0;
  let eventIds = [];
  let countByEventType;

  const removeEntityFilter = () => {
    filterEntity = "";
    filteredEventData = eventData;
  };

  const removeEventFilter = () => {
    filterEvent = "";
    filteredEventData = eventData;
  };

  $: {
    filterEntityTypes = uniq(
      eventData.map(event => event.entities.map(entity => entity.type)).flat(),
    ).sort();
    filterEventTypes = uniq(eventData.map(event => event.event.name)).sort();

    if (!filterEntity) {
      filteredEventData = eventData;
    } else {
      filteredEventData = eventData.filter(event => {
        return event.entities.filter(entity => entity.type === filterEntity).length > 0;
      });
    }

    if (filterEvent) {
      filteredEventData = filteredEventData.filter(event => event.event.name === filterEvent);
    }

    eventIds = filteredEventData.map(d => {
      return `'${d.event.id}'`;
    });

    eventCount = eventIds.length;

    countByEventType = {};
    filteredEventData.forEach(d => {
      sessionId = d.context.sessionId;
      if (countByEventType[d.event.name]) {
        countByEventType[d.event.name]++;
      } else {
        countByEventType[d.event.name] = 1;
      }
    });
  }
</script>

<style>
  .event-details-logo {
    position: fixed;
    left: 2%;
    bottom: 2%;
    z-index: 1000;
    background-color: aqua;
    box-shadow: 0 0 3px 3px rgba(0, 0, 0, 0.4);
    border-radius: 10px;
    cursor: pointer;
  }

  .event-details-logo-inset {
    background-color: aliceblue;
    box-shadow: 0 0 3px 3px rgba(0, 0, 0, 0.4);
    border-radius: 10px;
    cursor: pointer;
  }

  .event-details-pane {
    background: #efefef;
    min-width: 200px;
    max-height: calc(100% - 110px);
    width: 50%;
    box-shadow: 0 0 3px 3px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    position: fixed;
    overflow-y: scroll;
    top: 105px;
    right: 0;
    transition: right 0.3s;
    padding-bottom: 110px;
  }

  .hide-event-details-pane {
    right: -45%;
  }

  .event-details {
    position: relative;
    color: black;
  }
</style>

<div>
  {#if collapsed}
    <div
      data-testid="event-tracker-bubbles"
      class="event-details-logo fizz-styles"
      on:click={toggleCollapse}
    >
      <Icon width="50" height="50" type="Bubbles" />
    </div>
  {:else}
    <div
      class="event-details-pane fizz-styles"
      class:hide-event-details-pane={!show}
      transition:fly|local={{ y: 200, duration: 500 }}
    >
      <header class="fizz-flex-row fizz-inset-square-8">
        <button class="fizz-button fizz-button-icon-left fizz-flex-row-4" on:click={slideOver}>
          <Icon width="16" height="16" type={show ? "ArrowRight" : "ArrowLeft"} />
        </button>
        <h2 class="fizz-heading-2 fizz-flex-item">Tracking Events</h2>
        <div class="event-details-logo-inset" on:click={toggleCollapse}>
          <Icon width="32" height="32" type="Bubbles" />
        </div>
      </header>
      <section class="fizz-inset-square-8">
        <div class="fizz-stack-8">
          <button
            class="fizz-button fizz-button-icon-left fizz-flex-row-4 fizz-red-300"
            disabled={!eventData.length}
            on:click={clearEvents}
          >
            <Icon width="16" height="16" type="Trash" />
            <span>Clear Events</span>
          </button>
        </div>
        <div class="fizz-flex-row">
          <div>
            <div class="fizz-flex-row">
              <label class="fizz-label" for="event-filter">Filter By Event</label>
              {#if filterEvent}
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                  href="#"
                  on:click|preventDefault={removeEventFilter}
                  data-testid="clear-event-filter"
                >
                  Clear filter
                </a>
              {/if}
            </div>
            <select class="fizz-select" data-testid="event-type-filter" bind:value={filterEvent}>
              <option value="" disabled />
              {#each filterEventTypes as option}
                <option value={option}>{option}</option>
              {/each}
            </select>
          </div>
          <div>
            <div class="fizz-flex-row">
              <label class="fizz-label" for="entity-filter">Filter By Entity</label>
              {#if filterEntity}
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                  href="#"
                  on:click|preventDefault={removeEntityFilter}
                  data-testid="clear-entity-filter"
                >
                  Clear filter
                </a>
              {/if}
            </div>
            <select class="fizz-select" data-testid="entity-type-filter" bind:value={filterEntity}>
              <option value="" disabled />
              {#each filterEntityTypes as option}
                <option value={option}>{option}</option>
              {/each}
            </select>
          </div>
        </div>
      </section>
      <section class="fizz-inset-square-8">
        {#if !filteredEventData.length}
          <div class="proviLoader" transition:fade|local>
            <ul class="proviLoader__blast" />
            <div class="proviLoader__wineglass proviLoader__wineglass-left">
              <div class="proviLoader__top" />
            </div>
            <div class="proviLoader__wineglass proviLoader__wineglass-right">
              <div class="proviLoader__top" />
            </div>
          </div>
        {:else}
          <details
            data-testid="event-tracker-summary"
            class="event-details fizz-card-floating fizz-card-padded fizz-stack-12"
          >
            <summary class="fizz-summary">Event Details Summary</summary>
            <div class="fizz-card">
              <p><strong>Session Id:</strong> {sessionId}</p>
              <p><strong>Total Event Count:</strong> {eventCount}</p>

              {#each Object.entries(countByEventType) as eventType}
                <p><strong>{eventType[0]}:</strong> {eventType[1]}</p>
              {/each}

              <details class="event-details">
                <summary><strong>Event Ids</strong></summary>
                <pre>
                  <p>{eventIds}</p>
                </pre>
              </details>
            </div>
          </details>

          {#each filteredEventData as d}
            <details
              data-testid="event-card"
              class="event-details fizz-card-floating fizz-card-padded fizz-stack-12"
              transition:fade|local
            >
              <summary class="fizz-summary fizz-flex-row">
                <span>
                  <Icon width="16" height="16" type="EventTracking" />
                </span>
                <strong>{d.event.name}</strong>
                <span class="fizz-text-subdued">
                  {d.entities
                    .map(e => e.type)
                    .filter(e => !!e)
                    .join(" - ")}
                </span>
              </summary>
              <dl>
                {#each Object.entries(d) as eventEntry}
                  <dd>
                    <details>
                      <summary class="fizz-summary">
                        <strong>{eventEntry[0]}</strong>
                      </summary>
                      {#if eventEntry[0] === "entities"}
                        <dl class="fizz-inset-square-8">
                          {#each eventEntry[1] as entitiesEntry}
                            <dd>
                              <details>
                                <summary class="fizz-summary">
                                  <strong>{entitiesEntry.type}</strong>
                                </summary>
                                <pre>
                                  {JSON.stringify(entitiesEntry, null, "  ")}
                                </pre>
                              </details>
                            </dd>
                          {/each}
                        </dl>
                      {:else}
                        <pre>
                        {JSON.stringify(eventEntry[1], null, "  ")}
                      </pre>
                      {/if}
                    </details>
                  </dd>
                {/each}
              </dl>
            </details>
          {/each}
        {/if}
      </section>
    </div>
  {/if}
</div>
